import React, { memo, useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';

import { Box } from '@mui/material';
import FormGenerator from '@sharedCR/common/formGenerator';
import { FormNames } from 'app/services/autoLockForms/autoLockForms.constants';
import _ from 'lodash';

import { PropertyType } from 'constants/loanApplication';

import {
  selectIsPropertyEnrichmentLoading,
  selectPropertyEnrichmentData,
} from 'store/loanApplications/selectors';

import { defaultForm, pagesPropTypes } from '../MainContent.constants';
import { boxStyle } from '../MainContent.styles';
import EnrichmentFieldIndicators from '../Property/components/EnrichmentFieldIndicators';
import { getProjectEnrichmentValuesMap } from '../Property/enrichmentFieldMapping';

import { getProjectConfig } from './Project.config';

const CONDO_COOP_PROPERTY_TYPES = [PropertyType.CONDO, PropertyType.COOP];

const Project = ({
  changedFields,
  isLoading,
  isSaving,
  form = defaultForm,
  validationErrors,
  validateField,
  handleChange,
  setEntityConfig,
  setInForm,
  className,
  applicationData,
  staticValidateField,
  pendingChanges,
  disabledPendingChangesHighlight,
}) => {
  const formContainerRef = useRef(null);
  const enrichmentData = useSelector(selectPropertyEnrichmentData);
  const isEnrichmentLoading = useSelector(selectIsPropertyEnrichmentLoading);

  const enrichmentValuesMap = useMemo(
    () => getProjectEnrichmentValuesMap(enrichmentData),
    [enrichmentData],
  );

  const isFormDataEmpty = _.isEmpty(form);
  const entityData = isFormDataEmpty ? applicationData : form;
  const propertyTypeValue = entityData?.propertyType?.value;
  const isCondoOrCoOp = CONDO_COOP_PROPERTY_TYPES.includes(propertyTypeValue);

  const config = useMemo(() => getProjectConfig({ isCondoOrCoOp }), [isCondoOrCoOp]);

  useEffect(() => {
    setEntityConfig(config);
  }, [setEntityConfig, config]);

  return (
    <Box className={className}>
      <Box ref={formContainerRef} sx={boxStyle}>
        <EnrichmentFieldIndicators
          containerRef={formContainerRef}
          enrichmentValuesMap={enrichmentValuesMap}
          form={form}
          isLoading={isEnrichmentLoading}
          setInForm={setInForm}
          validateField={validateField}
        />
        <FormGenerator
          isNestedForm
          changedFields={changedFields}
          config={config}
          disabledPendingChangesHighlight={disabledPendingChangesHighlight}
          form={form}
          formName={FormNames.PROJECT}
          handleChange={handleChange}
          isLoading={isSaving || isLoading}
          numberOfColumns={4}
          pendingChanges={pendingChanges}
          setInForm={setInForm}
          staticValidateField={staticValidateField}
          validateField={validateField}
          validationErrors={validationErrors}
        />
      </Box>
    </Box>
  );
};

Project.propTypes = pagesPropTypes;

export default memo(Project);
